'use client';

import { useCommendaOSList } from '@commenda/api/commenda-os-dsl';
import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import {
  Button,
  Icons,
  LoadingIndicator,
  MultiSelect,
  SingleCombobox,
} from '@commenda/kakashi';
import type {
  DSLFile,
  FormDSL,
  FormDSLContext,
  ValidationRules,
} from '@commenda/types';
import { FormsTemplateType } from '@commenda/types';
import type { DSLContext } from '@commenda/types/src/commenda-os-dsl';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PERSON_LABELS_KEY } from '../../dsl-constants';
import { entryCoverageError } from '../../utils/validation';
import { useComponentRegistry } from '../../hooks/ComponentRegistryContext';
import { FormContext, type FormContextType } from '../../hooks/FormContext';
import { useEntryCompleteness } from '../../hooks/useEntryCompleteness';
import {
  buildComponentMap,
  getRepeatableSaveKeys,
} from '../../utils/dsl-traversal';
import { EntryCompletenessHelper } from '../../utils/entry-completeness-helper';
import type {
  EntryCompletenessTarget,
  LinkedFormCompletenessSource,
} from '../../utils/entry-completeness.types';
import {
  getEntryTitleTemplate,
  resolveEntryTitle,
} from '../../utils/entry-title';
import { LinkedFormDslHelper } from '../../utils/linked-form-dsl-helper';
import { EntryCompletenessStatusText } from './EntryCompletenessStatusText';
import { LinkedFormRenderer } from './LinkedFormRenderer';
import { NestedFormDrawer } from './NestedFormDrawer';

/**
 * Wrapper that injects per-person dslContext into a linked form. For existing
 * persons (numeric id), sets `directorId` so AutoDetectField can prefill from
 * Commenda OS. For new persons (id starts with `new-`), strips any inherited
 * `directorId` so submit runs CREATE rather than UPDATE on a stale id.
 */
const KeyPersonsLinkedFormWrapper = ({
  dslContext,
  editingPersonId,
  children,
}: {
  dslContext?: FormDSLContext;
  editingPersonId: string | null;
  children: React.ReactNode;
}) => {
  const parentContext = useContext(FormContext);

  const isNewPerson =
    editingPersonId != null && editingPersonId.startsWith('new-');
  const isExisting =
    editingPersonId != null && !editingPersonId.startsWith('new-');
  const directorId = isExisting ? Number(editingPersonId) : undefined;

  const enhancedDslContext = useMemo(() => {
    if (!dslContext) return dslContext;
    if (isExisting && directorId) {
      return {
        ...dslContext,
        directorId,
      } as FormDSLContext;
    }
    if (isNewPerson) {
      const { directorId: _omit, ...rest } = dslContext as FormDSLContext & {
        directorId?: number;
      };
      return rest as FormDSLContext;
    }
    return dslContext;
  }, [dslContext, isExisting, isNewPerson, directorId]);

  const enhancedContext = useMemo((): FormContextType => {
    return {
      ...parentContext,
      dslContext: enhancedDslContext,
    };
  }, [parentContext, enhancedDslContext]);

  return (
    <FormContext.Provider value={enhancedContext}>
      {children}
    </FormContext.Provider>
  );
};

const pluralise = (count: number, noun: string, nounPlural?: string): string =>
  count > 1 ? (nounPlural ?? `${noun}s`) : noun;

export const KeyPersonsComponent = ({
  fieldId,
  label,
  description,
  validation,
  props,
}: {
  fieldId?: string;
  label?: string;
  description?: string;
  validation?: { rules?: ValidationRules };
  props?: Record<string, unknown>;
}) => {
  const {
    formData,
    setFormData,
    updateField,
    errors,
    setErrors,
    dslContext,
    setNestedFormOpen,
    isPreview,
    registerPreviewFillHandler,
  } = useContext(FormContext);
  const componentRegistry = useComponentRegistry();

  const mode = (props?.mode as 'select-add' | 'view-edit') ?? 'select-add';
  const keyId = props?.keyId as string | undefined;
  const minSelection = (props?.minSelection as number) ?? 1;
  const maxSelection = (props?.maxSelection as number) ?? 0;
  const addNewLabel = (props?.addNewLabel as string) || 'Add new key person';
  // The count hint and the selection error both read it, so a form about directors does not
  // tell people to pick key persons.
  const noun = (props?.noun as string) || 'key person';
  const nounPlural = props?.nounPlural as string | undefined;
  // The person template is shared, so it hides what this context does not need.
  const personContext = props?.personContext as string | undefined;
  const selectPlaceholder =
    (props?.selectPlaceholder as string) ||
    (maxSelection === 1 ? `Select a ${noun}...` : 'Select key persons...');
  const effectiveDescription =
    description ?? (maxSelection === 1 ? 'Select a key person' : undefined);
  const entryTitleTemplateOverride = props?.entryTitleTemplate;

  const isViewEditMode = mode === 'view-edit';

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPersonId, setEditingPersonId] = useState<string | null>(null);
  const [newPersonCounter, setNewPersonCounter] = useState(0);

  // In select-add: fetch company key persons. In view-edit: fetch corporation's linked directors.
  const {
    data: keyPersonsList,
    isLoading: isLoadingPersons,
    mutate: mutateKeyPersonsList,
  } = useCommendaOSList<Record<string, unknown>>(
    isViewEditMode ? 'keyPersonRelationship' : 'keyPerson',
    dslContext as { companyId: number; corporationId?: number } | undefined,
    isViewEditMode ? 'corporation.keyPersonRelationships' : 'company.keyPersons'
  );

  // Fetch linked form template
  const {
    key: formKey,
    isLoading: isLoadingFormKey,
    error: formKeyError,
  } = useFormTemplateKey(keyId ?? null);
  const {
    template: formTemplate,
    isLoading: isLoadingTemplate,
    error: formTemplateError,
  } = useFormsTemplate(formKey?.liveFormId ?? null);

  // Data shape: { selectedIds: string[], entries: Record<personId, formData> }
  // Only recompute when the specific field changes, not on every formData change
  const rawFieldData = formData[fieldId ?? ''];
  const storedData = useMemo(() => {
    if (
      rawFieldData &&
      typeof rawFieldData === 'object' &&
      !Array.isArray(rawFieldData)
    ) {
      const d = rawFieldData as {
        selectedIds?: string[];
        entries?: Record<string, Record<string, unknown>>;
      };
      return {
        selectedIds: d.selectedIds ?? [],
        entries: d.entries ?? {},
      };
    }
    return {
      selectedIds: [] as string[],
      entries: {} as Record<string, Record<string, unknown>>,
    };
  }, [rawFieldData]);

  type KeyPersonsField = {
    selectedIds: string[];
    entries: Record<string, Record<string, unknown>>;
  };
  // Merge KeyPersons field using latest form state (avoids stale closure).
  // Patch can be an object or a function (current) => partial for add/remove.
  const updateStoredData = useCallback(
    (
      patch:
        | {
            selectedIds?: string[];
            entries?: Record<string, Record<string, unknown>>;
          }
        | ((current: KeyPersonsField) => Partial<KeyPersonsField>)
    ) => {
      if (!fieldId) return;
      setFormData((prev) => {
        const current = prev[fieldId] as
          | {
              selectedIds?: string[];
              entries?: Record<string, Record<string, unknown>>;
            }
          | undefined;
        const base: KeyPersonsField =
          current && typeof current === 'object' && !Array.isArray(current)
            ? {
                selectedIds: current.selectedIds ?? [],
                entries: current.entries ?? {},
              }
            : { selectedIds: [], entries: {} };
        const applied = typeof patch === 'function' ? patch(base) : patch;
        return {
          ...prev,
          [fieldId]: {
            // Carried, not rebuilt: their own effect publishes the labels, and every other
            // write would drop them.
            ...(current && typeof current === 'object' && !Array.isArray(current)
              ? { [PERSON_LABELS_KEY]: (current as Record<string, unknown>)[PERSON_LABELS_KEY] }
              : {}),
            selectedIds: applied.selectedIds ?? base.selectedIds,
            entries: applied.entries ?? base.entries,
          },
        };
      });
    },
    [fieldId, setFormData]
  );

  // In view-edit mode, auto-populate selectedIds from corporation's directors when list loads.
  // Merge API ids with any local-only ids (temp `new-*` or real directorId not yet in the
  // refetched list). Do NOT replace on length mismatch alone — that would drop an in-progress
  // "add new key person" row (4 selected vs 3 from API).
  useEffect(() => {
    if (
      !fieldId ||
      !isViewEditMode ||
      !keyPersonsList ||
      !Array.isArray(keyPersonsList) ||
      keyPersonsList.length === 0
    ) {
      return;
    }
    const apiIds = keyPersonsList.map((p) =>
      String((p as { id?: number }).id ?? '')
    );
    if (apiIds.length === 0) return;
    const current = formData[fieldId] as
      | { selectedIds?: string[]; entries?: Record<string, unknown> }
      | undefined;
    const currentIds = current?.selectedIds ?? [];
    const apiSet = new Set(apiIds);

    if (currentIds.length === 0) {
      updateField(fieldId, {
        ...current,
        selectedIds: apiIds,
        entries: current?.entries ?? {},
      });
      return;
    }

    const extras = currentIds.filter((id) => !apiSet.has(String(id)));
    const merged = [...apiIds, ...extras];

    const same =
      merged.length === currentIds.length &&
      merged.every((id, i) => id === currentIds[i]);
    if (!same) {
      updateField(fieldId, {
        ...current,
        selectedIds: merged,
        entries: current?.entries ?? {},
      });
    }
  }, [fieldId, isViewEditMode, keyPersonsList, updateField, formData]);

  // Build MultiSelect options from key persons list
  const selectOptions = useMemo(() => {
    if (!keyPersonsList || !Array.isArray(keyPersonsList)) return [];
    return keyPersonsList.map((person) => {
      const firstName = (person.firstName as string) || '';
      const lastName = (person.lastName as string) || '';
      const id = String(person.id ?? '');
      return {
        value: id,
        label: `${firstName} ${lastName}`.trim() || `Key Person ${id}`,
      };
    });
  }, [keyPersonsList]);

  const handleSelectionChange = useCallback(
    (newValues: string[]) => {
      const optionValues = new Set(selectOptions.map((o) => o.value));
      const preserved = storedData.selectedIds.filter(
        (id) => !optionValues.has(id)
      );
      const combined = [...preserved, ...newValues];
      const seen = new Set<string>();
      const merged = combined.filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      updateStoredData({ selectedIds: merged });
    },
    [updateStoredData, selectOptions, storedData.selectedIds]
  );

  const handleSingleSelectionChange = useCallback(
    (newValue: string) => {
      handleSelectionChange(newValue ? [newValue] : []);
    },
    [handleSelectionChange]
  );

  // Preview self-fill: select the first loaded key person — a real record only
  // this component can see, so the preview Fill button delegates to us.
  useEffect(() => {
    if (!isPreview || !registerPreviewFillHandler || !fieldId) return undefined;
    registerPreviewFillHandler(fieldId, () => {
      if (storedData.selectedIds.length > 0) return { kind: 'already-filled' };
      if (isLoadingPersons) return { kind: 'loading' };
      if (selectOptions.length === 0) {
        return {
          kind: 'skipped',
          reason: 'no key persons on this corporation — add one manually',
        };
      }
      handleSelectionChange([selectOptions[0].value]);
      return { kind: 'filled' };
    });
    return () => registerPreviewFillHandler(fieldId, null);
  }, [
    isPreview,
    registerPreviewFillHandler,
    fieldId,
    storedData.selectedIds.length,
    isLoadingPersons,
    selectOptions,
    handleSelectionChange,
  ]);

  const handleAddNew = useCallback(() => {
    const newId = `new-${Date.now()}-${newPersonCounter}`;
    setNewPersonCounter((prev) => prev + 1);
    // Added on save, not now: listing someone when the drawer opens leaves an unfilled row
    // behind if it is closed again, and counts towards the minimum.
    setEditingPersonId(newId);
    setDrawerOpen(true);
    if (fieldId) setNestedFormOpen({ fieldId, editingIndex: null });
  }, [newPersonCounter, fieldId, setNestedFormOpen]);

  const handleRemovePerson = useCallback(
    (personId: string) => {
      updateStoredData((current) => {
        const newIds = current.selectedIds.filter((id) => id !== personId);
        const newEntries = { ...current.entries };
        delete newEntries[personId];
        return { selectedIds: newIds, entries: newEntries };
      });
    },
    [updateStoredData]
  );

  const openDrawer = useCallback(
    (personId: string) => {
      setEditingPersonId(personId);
      setDrawerOpen(true);
      if (fieldId) setNestedFormOpen({ fieldId, editingIndex: null });
    },
    [fieldId, setNestedFormOpen]
  );

  const closeDrawer = useCallback(() => {
    if (editingPersonId?.startsWith('new-')) {
      updateStoredData((current) => {
        if (!current.entries[editingPersonId]) {
          const newIds = current.selectedIds.filter(
            (id) => id !== editingPersonId
          );
          return { selectedIds: newIds };
        }
        return current;
      });
    }
    setDrawerOpen(false);
    setEditingPersonId(null);
    setNestedFormOpen(null);
  }, [editingPersonId, updateStoredData, setNestedFormOpen]);

  const handleFormSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      if (!editingPersonId) return;

      // If a new person was created via Commenda OS, replace the temporary
      // "new-xxx" key with the real directorId in both selectedIds and entries.
      const createdDirectorId = data.__createdDirectorId as number | undefined;
      const { __createdDirectorId: _stripId, ...cleanData } = data;

      const labelMap: Record<string, string> = {};
      const fieldMappings: Record<
        string,
        {
          entity: string;
          createPath?: string;
          updatePath?: string;
          readPath?: string;
          filters?: Array<{ key: string; value: string }>;
        }
      > = {};
      if (formTemplate?.dsl) {
        const compMap = buildComponentMap(
          formTemplate.dsl as unknown as DSLFile
        );
        Object.entries(compMap).forEach(([fid, comp]) => {
          if (comp.label) labelMap[fid] = comp.label;
          const cos = (
            comp as unknown as {
              dataSource?: {
                commendaOS?: {
                  entity?: string;
                  field?: Record<string, unknown>;
                  filters?: Record<string, unknown>;
                };
              };
            }
          ).dataSource?.commendaOS;
          if (cos?.entity && cos?.field) {
            const field = cos.field as {
              read?: string;
              create?: string;
              update?: string;
            };
            fieldMappings[fid] = {
              entity: cos.entity,
              createPath: field.create,
              updatePath: field.update,
              readPath: field.read,
              filters: cos.filters
                ? Object.entries(cos.filters).map(([k, v]) => ({
                    key: k,
                    value: String(v),
                  }))
                : undefined,
            };
          }
        });
      }

      const cosSubmitted = !!createdDirectorId;

      const entryWithMeta = {
        ...cleanData,
        __meta: {
          labels: labelMap,
          formName: formTemplate?.name ?? label ?? '',
          __cosSubmitted: cosSubmitted,
          fieldMappings,
          repeatableKeys: formTemplate?.dsl
            ? getRepeatableSaveKeys(formTemplate.dsl as unknown as DSLFile)
            : [],
        },
      };

      if (createdDirectorId && editingPersonId.startsWith('new-')) {
        const realId = String(createdDirectorId);
        updateStoredData((current) => {
          const newSelectedIds = current.selectedIds.includes(editingPersonId)
            ? current.selectedIds.map((id) =>
                id === editingPersonId ? realId : id
              )
            : [...current.selectedIds, realId];
          const { [editingPersonId]: _oldEntry, ...restEntries } =
            current.entries;
          return {
            selectedIds: newSelectedIds,
            entries: { ...restEntries, [realId]: entryWithMeta },
          };
        });
        mutateKeyPersonsList();
      } else {
        // A person saved without reaching the entity still belongs in the list.
        updateStoredData((current) => ({
          selectedIds: current.selectedIds.includes(editingPersonId)
            ? current.selectedIds
            : [...current.selectedIds, editingPersonId],
          entries: { ...current.entries, [editingPersonId]: entryWithMeta },
        }));
      }

      if (fieldId) {
        const { [fieldId]: _removed, ...restErrors } = errors;
        setErrors(restErrors);
      }
      closeDrawer();
    },
    [
      editingPersonId,
      formTemplate,
      label,
      updateStoredData,
      fieldId,
      errors,
      setErrors,
      closeDrawer,
      mutateKeyPersonsList,
    ]
  );

  const getPersonLabel = useCallback(
    (personId: string, index: number): string => {
      const option = selectOptions.find((o) => o.value === personId);
      if (option?.label) return option.label;

      const linkedFormDSL = formTemplate?.dsl as FormDSL | undefined;
      const entryTitle = resolveEntryTitle({
        entry: storedData.entries[personId],
        template: getEntryTitleTemplate(
          entryTitleTemplateOverride,
          linkedFormDSL
        ),
        dsl: linkedFormDSL,
      });
      if (entryTitle) return entryTitle;

      return `New ${noun} (${index + 1})`;
    },
    [
      noun,
      storedData.entries,
      selectOptions,
      formTemplate,
      entryTitleTemplateOverride,
    ]
  );

  // Publish each selected person's name alongside the selection: a person chosen from the
  // company authors no entry, so their name lives only in the list fetched here.
  useEffect(() => {
    if (!fieldId) return;
    const labels: Record<string, string> = {};
    storedData.selectedIds.forEach((personId, index) => {
      labels[personId] = getPersonLabel(personId, index);
    });
    const current = (rawFieldData as Record<string, unknown> | undefined)?.[
      PERSON_LABELS_KEY
    ] as Record<string, string> | undefined;
    const unchanged =
      current &&
      Object.keys(labels).length === Object.keys(current).length &&
      Object.entries(labels).every(([id, name]) => current[id] === name);
    if (unchanged) return;
    setFormData((prev) => {
      const field = prev[fieldId];
      if (!field || typeof field !== 'object' || Array.isArray(field)) return prev;
      return { ...prev, [fieldId]: { ...field, [PERSON_LABELS_KEY]: labels } };
    });
  }, [fieldId, storedData.selectedIds, getPersonLabel, rawFieldData, setFormData]);

  const linkedForm = useMemo(
    (): LinkedFormCompletenessSource | null =>
      keyId
        ? {
            dsl: formTemplate?.dsl
              ? LinkedFormDslHelper.normalize(formTemplate.dsl, formTemplate.type)
              : null,
            isLoading: isLoadingFormKey || isLoadingTemplate,
            hasFailed: !!formKeyError || !!formTemplateError,
          }
        : null,
    [
      keyId,
      formTemplate,
      isLoadingFormKey,
      isLoadingTemplate,
      formKeyError,
      formTemplateError,
    ]
  );
  // Only people picked from the list: a new person's drawer already enforced its form on save.
  const completenessTargets = useMemo(
    (): EntryCompletenessTarget[] =>
      storedData.selectedIds
        .filter((personId) => !personId.startsWith('new-'))
        .map((personId) => {
          const entryAnswers = EntryCompletenessHelper.getEntryAnswers(
            storedData.entries[personId]
          );
          return {
            entryId: personId,
            readContext: dslContext?.companyId
              ? // A FormDSLContext with a companyId is a DSLContext.
                ({ ...dslContext, directorId: Number(personId) } as DSLContext)
              : undefined,
            values: { ...(personContext ? { personContext } : {}), ...entryAnswers },
          };
        }),
    [storedData.selectedIds, storedData.entries, dslContext, personContext]
  );
  const { statusByEntryId } = useEntryCompleteness({
    linkedForm,
    targets: completenessTargets,
  });

  const isPersonFilled = useCallback(
    (personId: string): boolean =>
      personId.startsWith('new-')
        ? !!storedData.entries[personId]
        : !statusByEntryId[personId],
    [storedData.entries, statusByEntryId]
  );

  const completenessError = EntryCompletenessHelper.getBlockingError(
    storedData.selectedIds.flatMap((personId, index) => {
      const status = statusByEntryId[personId];
      return status ? [{ name: getPersonLabel(personId, index), status }] : [];
    })
  );

  const canAddMore =
    maxSelection === 0 || storedData.selectedIds.length < maxSelection;
  const isLoading = isLoadingPersons;

  // Without this the configured count is a hint the step advances past.
  const selected = storedData.selectedIds.length;
  const exactly = minSelection > 0 && minSelection === maxSelection;
  const countError =
    minSelection > 0 && selected < minSelection
      ? maxSelection === 1
        ? 'Select a key person.'
        : `Select ${exactly ? 'exactly' : 'at least'} ${String(minSelection)} ${pluralise(minSelection, noun, nounPlural)}.`
      : maxSelection > 0 && selected > maxSelection
        ? `Select ${exactly ? 'exactly' : 'at most'} ${String(maxSelection)} ${pluralise(maxSelection, noun, nounPlural)}.`
        : undefined;

  // The node owns this one error slot, so the selection-wide rule is evaluated here rather
  // than by the form's validator; two writers would take turns clearing each other. Count
  // first, because asking for offices before anyone is listed is noise.
  const selectionError = isViewEditMode
    ? completenessError
    : (countError ??
      entryCoverageError(
        validation?.rules?.entryCoverage,
        rawFieldData,
        () => true
      ) ??
      completenessError);

  // Mirror it into the form errors map so submission is gated the same way every
  // other field is; the drawer clears the key on save, and this puts it back.
  useEffect(() => {
    if (!fieldId) return;
    const current = errors[fieldId];
    if (selectionError) {
      if (current !== selectionError)
        setErrors({ ...errors, [fieldId]: selectionError });
    } else if (current !== undefined) {
      const cleared = { ...errors };
      delete cleared[fieldId];
      setErrors(cleared);
    }
  }, [fieldId, selectionError, errors, setErrors]);

  const fieldError = fieldId ? errors[fieldId] : undefined;
  const visibleFieldError =
    maxSelection === 1 && fieldError === countError ? undefined : fieldError;
  const emptyDefaults = useRef<Record<string, unknown>>({});
  const defaultValues = useMemo(() => {
    const context = personContext ? { personContext } : undefined;
    if (editingPersonId && storedData.entries[editingPersonId]) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __meta: _ignored, ...rest } = storedData.entries[editingPersonId];
      return { ...context, ...rest };
    }
    return context ?? emptyDefaults.current;
  }, [editingPersonId, storedData.entries, personContext]);

  if (!fieldId) return null;

  if (!dslContext?.companyId) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        <div className="p-4 border border-border-warning rounded-md bg-bg-warning-subtle">
          <p className="text-sm text-text-warning">
            Company context required to load key persons.
          </p>
        </div>
      </div>
    );
  }

  if (isViewEditMode && !dslContext?.corporationId) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        <div className="p-4 border border-border-warning rounded-md bg-bg-warning-subtle">
          <p className="text-sm text-text-warning">
            Corporation context required to list directors for this entity.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="rounded-lg border border-border-secondary bg-bg-primary overflow-hidden mb-4"
        data-field-id={fieldId}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6">
          <div className="flex flex-col gap-1 min-w-0">
            {label && (
              <p className="txt-medium-medium text-text-primary">{label}</p>
            )}
            {effectiveDescription && (
              <p className="txt-small-regular text-text-secondary">
                {effectiveDescription}
              </p>
            )}
            {visibleFieldError && (
              <p className="mt-1 flex items-center gap-1.5 text-sm text-text-error">
                <Icons.AlertCircle className="h-4 w-4 shrink-0" />
                {visibleFieldError}
              </p>
            )}
          </div>
        </div>

        {/* In view-edit mode, show loading in the list area when fetching corporation directors */}
        {isViewEditMode && isLoading && (
          <div className="px-6 pb-4">
            <div className="flex items-center gap-2 py-2">
              <LoadingIndicator size={16} />
              <span className="text-sm text-text-tertiary">
                Loading directors...
              </span>
            </div>
          </div>
        )}
        {/* In view-edit mode, show Add button to open nested form for adding a new director */}
        {isViewEditMode && !isLoading && keyId && (
          <div className="px-6 pb-4">
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddNew}
              disabled={!canAddMore}
            >
              <Icons.Plus className="w-3.5 h-3.5 mr-1" />
              {addNewLabel}
            </Button>
          </div>
        )}
        {/* Selection (hidden in view-edit mode; list is fixed from corporation) */}
        {!isViewEditMode && (
          <div className="px-6 pb-4">
            {isLoading ? (
              <div className="flex items-center gap-2 py-2">
                <LoadingIndicator size={16} />
                <span className="text-sm text-text-tertiary">
                  Loading key persons...
                </span>
              </div>
            ) : (
              // Adding someone is the main thing people come here to do, so it gets its
              // own button beside the select rather than a second entry point inside it.
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  {maxSelection === 1 ? (
                    <SingleCombobox
                      id={`${fieldId}-select`}
                      options={selectOptions}
                      setValue={handleSingleSelectionChange}
                      value={
                        storedData.selectedIds.find((id) =>
                          selectOptions.some((option) => option.value === id)
                        ) ?? ''
                      }
                      placeholder={selectPlaceholder}
                      canClearSelection
                    />
                  ) : (
                    <MultiSelect
                      id={`${fieldId}-multiselect`}
                      options={selectOptions}
                      onValueChange={handleSelectionChange}
                      defaultValue={storedData.selectedIds}
                      placeholder={selectPlaceholder}
                      hideSelectAll
                      hideTags
                      maxSelectable={maxSelection === 0 ? null : maxSelection}
                    />
                  )}
                </div>
                {keyId && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleAddNew}
                    disabled={!canAddMore}
                    className="shrink-0"
                  >
                    <Icons.Plus className="w-3.5 h-3.5 mr-1" />
                    {addNewLabel}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Selected persons list */}
        {storedData.selectedIds.length > 0 && (
          <div className="px-6 pb-6">
            {storedData.selectedIds.map((personId, index) => (
              <div
                key={personId}
                className={`flex items-center gap-2 py-4 ${
                  index < storedData.selectedIds.length - 1
                    ? 'border-b border-border-secondary'
                    : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="txt-small-medium text-text-primary truncate">
                    {getPersonLabel(personId, index)}
                  </p>
                  {statusByEntryId[personId] ? (
                    <EntryCompletenessStatusText
                      status={statusByEntryId[personId]}
                    />
                  ) : (
                    !isPersonFilled(personId) &&
                    keyId && (
                      <p className="txt-xs-regular text-text-warning mt-0.5">
                        Form not filled yet
                      </p>
                    )
                  )}
                </div>
                {keyId && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openDrawer(personId)}
                  >
                    <Icons.Edit03 className="w-3.5 h-3.5 mr-1" />
                    {isPersonFilled(personId) ? 'Edit' : 'Fill'}
                  </Button>
                )}
                {!isViewEditMode && (
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-bg-secondary transition-colors shrink-0"
                    onClick={() => handleRemovePerson(personId)}
                    title="Remove"
                  >
                    <Icons.Trash01 className="w-4 h-4 text-text-tertiary" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Selection count hint (only in select-add mode). The error above is built from the same
            counts, so while it is showing this would repeat it word for word; the hint earns its
            place the rest of the time, notably as the only thing that names a maximum. */}
        {!isViewEditMode &&
          !visibleFieldError &&
          maxSelection !== 1 &&
          (minSelection > 0 || maxSelection > 0) && (
            <div className="px-6 pb-4">
              <p className="text-xs text-text-tertiary">
                {minSelection > 0 &&
                maxSelection > 0 &&
                minSelection === maxSelection
                  ? `Select exactly ${minSelection} ${pluralise(minSelection, noun, nounPlural)}`
                  : minSelection > 0 && maxSelection > 0
                    ? `Select ${minSelection} to ${maxSelection} ${pluralise(maxSelection, noun, nounPlural)}`
                    : minSelection > 0
                      ? `Select at least ${minSelection} ${pluralise(minSelection, noun, nounPlural)}`
                      : maxSelection > 0
                        ? `Select up to ${maxSelection} ${pluralise(maxSelection, noun, nounPlural)}`
                        : null}
              </p>
            </div>
          )}
      </div>

      {/* Drawer for per-person form */}
      <NestedFormDrawer isOpen={drawerOpen} close={closeDrawer}>
        {isLoadingTemplate && (
          <div className="flex items-center justify-center py-12 h-full">
            <LoadingIndicator />
          </div>
        )}

        {!keyId && !isLoadingTemplate && (
          <div className="text-center py-12 h-full flex flex-col items-center justify-center">
            <Icons.AlertCircle className="w-12 h-12 text-text-warning mx-auto mb-4" />
            <p className="txt-medium-medium text-text-primary mb-2">
              No form linked
            </p>
            <p className="txt-small-regular text-text-tertiary">
              Link a form template in the builder to define what to collect per
              key person.
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              onClick={closeDrawer}
            >
              Close
            </Button>
          </div>
        )}

        {keyId && !isLoadingTemplate && !formTemplate && (
          <div className="text-center py-12 h-full flex flex-col items-center justify-center">
            <Icons.AlertCircle className="w-12 h-12 text-text-error mx-auto mb-4" />
            <p className="txt-medium-medium text-text-primary mb-2">
              Form template not found
            </p>
            <p className="txt-small-regular text-text-tertiary">
              The linked form template could not be loaded. Please check the
              configuration.
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              onClick={closeDrawer}
            >
              Close
            </Button>
          </div>
        )}

        {formTemplate && !isLoadingTemplate && keyId && (
          <KeyPersonsLinkedFormWrapper
            dslContext={dslContext}
            editingPersonId={editingPersonId}
          >
            <LinkedFormRenderer
              type={formTemplate.type as FormsTemplateType}
              dsl={formTemplate.dsl as FormDSL}
              onClose={closeDrawer}
              onSubmit={handleFormSubmit}
              defaultValues={defaultValues}
              layoutSettings={{ hideTopHeader: true }}
              embedMode
              componentRegistry={componentRegistry}
            />
          </KeyPersonsLinkedFormWrapper>
        )}
      </NestedFormDrawer>
    </>
  );
};
